import { useMutation, useQuery } from '@tanstack/react-query';
import { showToast } from 'app/shared/util/toast.util';
import { queryClient } from 'app/app';
import { copyProjectDocument, copyProjectDocuments, deleteProjectDocument, deleteProjectDocumentAttachment, fetchProjectDocumentAttachments, fetchProjectDocuments, getProjectDocument, getProjectDocumentsVariant, moveOrderingDownProjectDocument, moveOrderingUp, saveProjectDocument, updateProjectDocumentAttachmentStatus, uploadProjectDocumentFile } from 'app/edozvola/cases/project-documents/service/project-document.service';
import { ProjectDocumentsModel } from 'app/edozvola/cases/project-documents/model/project-documents.model';
import { toast } from 'react-toastify';
import { PaginationState } from 'app/shared/pagination/pagination.model';
import { FileUploadModel } from 'app/shared/file-upload/model/file-upload.model';
import { messages, TABLE_QUERY_KEYS } from 'app/config/constants';
import { useLocation } from 'react-router-dom';
import success = toast.success;
import error = toast.error;

export const saveProjectDocumentQuery = (idCase: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (projectDocument: ProjectDocumentsModel) => {
      return saveProjectDocument(idCase, projectDocument);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.PROJECT_DOCUMENTS_TABLE] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchProjectDocumentsQuery = (pagination: PaginationState, idCase: string) => {
  const location = useLocation();
  const caseInPreview = location.pathname.includes('cases-preview');

  return useQuery({
    queryKey: [TABLE_QUERY_KEYS.PROJECT_DOCUMENTS_TABLE, { idCase: idCase, pagination: { ...pagination }, caseInPreview: caseInPreview }],
    queryFn: ({ signal }) =>
      fetchProjectDocuments(idCase, pagination.page, pagination.size, pagination.column, pagination.direction, caseInPreview),
  });
};

export const deleteProjectDocumentQuery = () => {
  return useMutation({
    mutationFn: (idProjectDocument: number) => deleteProjectDocument(idProjectDocument),
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.PROJECT_DOCUMENTS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchProjectDocumentQuery = (idProjectDocument: string) => {
  return useQuery({
    queryKey: ['project-document', idProjectDocument],
    queryFn: () => getProjectDocument(idProjectDocument),
    enabled: !!idProjectDocument,
  });
};

export const copyProjectDocumentQuery = () => {
  return useMutation({
    mutationFn: (idProjectDocument: number) => copyProjectDocument(idProjectDocument),
    onSuccess: () => {
      showToast(success, messages.DATA_COPIED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.PROJECT_DOCUMENTS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const uploadProjectDocumentAttachmentQuery = (idProjectDocument: string, onSuccessCallback, onErrorCallback) => {
  return useMutation({
    mutationFn: (file: FileUploadModel) => uploadProjectDocumentFile(idProjectDocument, file),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.PROJECT_DOCUMENTS_TABLE] });
      onSuccessCallback(variables);
    },
    onError: (data, variables, context) => onErrorCallback(data, variables),
  });
};

export const fetchProjectDocumentAttachmentsQuery = (pagination: PaginationState, idProjectDocument: string) => {
  const location = useLocation();
  const caseInPreview = location.pathname.includes('cases-preview');
  return useQuery({
    queryKey: [
      'project-document-attachments-table',
      { idProjectDocument: idProjectDocument, pagination: { ...pagination }, caseInPreview: caseInPreview },
    ],
    queryFn: ({ signal }) =>
      fetchProjectDocumentAttachments(
        idProjectDocument,
        pagination.page,
        pagination.size,
        pagination.column,
        pagination.direction,
        caseInPreview,
      ),
    enabled: idProjectDocument !== null,
  });
};

export const deleteProjectDocumentAttachmentQuery = () => {
  return useMutation({
    mutationFn: (idProjectDocumentAttachment: number) => deleteProjectDocumentAttachment(idProjectDocumentAttachment),
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: ['project-document-attachments-table'] });
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.PROJECT_DOCUMENTS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const updateProjectDocumentAttachmentStatusQuery = () => {
  return useMutation({
    mutationFn: (idProjectDocumentAttachment: number) => updateProjectDocumentAttachmentStatus(idProjectDocumentAttachment),
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: ['project-document-attachments-table'] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const copyProjectDocumentsFromCaseQuery = (caseUuid: string, onSuccessCallback: any) => {
  return useMutation({
    mutationFn: (caseUuidToCopyFrom: string) => copyProjectDocuments(caseUuid, caseUuidToCopyFrom),
    onSuccess: () => {
      onSuccessCallback();
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.PROJECT_DOCUMENTS_TABLE] });
      showToast(success, messages.DATA_COPIED);
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchProjectDocumentVariantQuery = (idCase: string, caseInPreview: boolean) => {
  return useQuery({
    queryKey: ['project-document-variant', { idCase: idCase, caseInPreview: caseInPreview }],
    queryFn: () => getProjectDocumentsVariant(idCase, caseInPreview),
  });
};
